"use client";
import "@/components/home/post.css";
import "@/components/home/blog.css";
import "@/components/home/blog-post-clerk.css";
import "@/components/ui/atoms/categories/data-display.css";

import { AuthorRow } from "@/components/home/author-row";
import { FigureCaptionLine } from "@/components/home/figure-caption";

import { LeaderList } from "@/components/home/leader-list";
import { PostMeta } from "@/components/home/post-meta";
import { SiteFooter } from "@/components/home/site-footer";
import { SiteNav } from "@/components/home/site-nav";
import { heroSerif } from "@/lib/theme/hero-serif";
import Image from "next/image";
import Link from "next/link";

import { memo } from "react";

const TOC = [
    { id: "the-machine", label: "The machine every dispute goes through" },
    { id: "writing-for-the-machine", label: "Writing for the machine" },
    { id: "round-two", label: "Round two is a different legal instrument" },
    { id: "what-we-dont-claim", label: "What we don't claim" },
] as const;

const FLOW = ["your letter", "a clerk, four minutes", "1 of 29 codes", "the ACDV form", "furnisher's database", "“verified”"] as const;

const LADDER = [
    { term: "Verified, no method shown", value: "MOV demand · §611(a)(6)–(7)" },
    { term: "45 days of silence", value: "Deletion demand · §611(a)(5)(A)" },
    { term: "Deleted, then it came back", value: "Reinsertion notice · §611(a)(5)(B)" },
    { term: "Verified with a weak method", value: "Furnisher dispute · §623(a)(8)" },
    { term: "Verifies everything, every time", value: "Counsel. Stop the loop." },
] as const;

const MATRIX = [
    { stage: "Item analysis", industry: "Eyeballing the report", disputewell: "48 cited rules" },
    { stage: "The claim", industry: "A paragraph of prose", disputewell: "A typed field contradiction" },
    { stage: "Round two", industry: "Round one, louder", disputewell: "A different legal instrument" },
    { stage: "Reinsertion", industry: "Caught when the client complains", disputewell: "Caught by the system, every import" },
    { stage: "Learning", industry: "Anecdotes in veterans' heads", disputewell: "An outcome ledger, per claim" },
] as const;

const DONT = [
    { title: "e-OSCAR access", body: "No one has it. Not us, not anyone selling you an integration. Everyone in this industry mails letters." },
    { title: "Full Metro 2 visibility", body: "Consumer reports are a projection of what furnishers file. We analyze the projection and keep a ledger of exactly what it cannot show." },
    { title: "Point predictions", body: "Our scores are calibrated rankings, not FICO point promises. Nobody honest promises points." },
] as const;

const AUTHORS = [
    { name: "Kane", src: "/graphics/dw-founder-kane2.webp" },
    { name: "Dylan", src: "/graphics/dw-founder-dylan.webp" },
] as const;

const RELATED = [
    { title: "Fluid grows with each round", dateISO: "2026-07-03T12:00:00.000Z", dateLabel: "Jul 3, 2026", category: "product", byline: "Kane · 5 min read" },
    { title: "Why we computed Metro 2 instead of templating it", dateISO: "2026-06-28T12:00:00.000Z", dateLabel: "Jun 28, 2026", category: "research", byline: "Dylan · 7 min read" },
    { title: "Round two is a different legal instrument", dateISO: "2026-06-24T12:00:00.000Z", dateLabel: "Jun 24, 2026", category: "research", byline: "Kane · 6 min read" },
] as const;

const AnchorHeading = memo(
    function AnchorHeading({ id, children }: { id: string; children: React.ReactNode }) {
        return (
            <h2 id={id} className="dw-post__h2 dw-text-heading" data-tone="foreground" data-component="AnchorHeading" data-location="src/components/home/blog-post-clerk.tsx">
                <a href={`#${id}`} className="dw-post__anchor" data-tone="muted" aria-label="Link to this section">
                    <span aria-hidden>#</span>
                </a>
                {children}
            </h2>
        );
    }
);

export const BlogPostClerk = memo(function BlogPostClerk() {
    return (
        <div className={heroSerif.variable} data-component="BlogPostClerk" data-location="src/components/home/blog-post-clerk.tsx">
            <SiteNav />

            <main id="main-content" className="dw-post__main dw-home__measure">
                <article className="dw-post__layout">
                    <div className="dw-post__rail">
                        <p className="dw-text-caption dw-tabular-nums" data-tone="muted">
                            <Link href="/blog" data-tone="muted">Blog</Link>
                            <span aria-hidden> / </span>
                            <Link href="/blog" data-tone="primary">research</Link>
                        </p>
                    </div>

                    <div className="dw-post__column">
                        <header className="dw-post__header">
                            <PostMeta dateISO="2026-07-05T12:00:00.000Z" dateLabel="Jul 5, 2026" category="research" variant="post" />
                            <h1 className="dw-text-hero" data-tone="foreground">The four-minute clerk</h1>
                            <AuthorRow authors={AUTHORS} read="9 min read" variant="post" />
                        </header>

                        <figure className="dw-post__hero">
                            <Image
                                src="/graphics/dw-burn-bg.webp"
                                alt="A prismatic beam burning across a deep blue field"
                                width={1448}
                                height={1086}
                                sizes="(min-width: 1024px) 48rem, 100vw"
                                className="dw-home__media"
                                priority
                            />
                        </figure>

                        <nav className="dw-post__toc dw-card" data-padding="lg" aria-label="Table of contents">
                            <p className="dw-text-caption dw-tabular-nums" data-tone="muted">Table of contents</p>
                            <ul>
                                {TOC.map((item) => (
                                    <li key={item.id}>
                                        <a href={`#${item.id}`} className="dw-post__toc-link dw-text-body" data-tone="foreground">{item.label}</a>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div className="dw-prose">
                            <p>
                                Every dispute in the United States goes through the same machine. A bureau clerk reads your letter with about
                                four minutes to spend and compresses it into one of 29 dispute codes. An automated form carries that code to
                                the furnisher, and the furnisher&apos;s default is to check the code against its own record and answer
                                verified. No one re-reads your prose.
                            </p>
                            <p>
                                Regulators&apos; studies found that roughly 90% of disputes collapse into the same four generic codes. Whatever
                                you wrote, however long you spent writing it, the machine reduces it to a category.
                            </p>
                            <p>
                                The industry&apos;s answer to this machine has always been volume. Dispute everything, reword it monthly, and
                                hope the deadlines break before the classifier does. The largest practitioner of that model ended with a $2.7
                                billion CFPB judgment.
                            </p>

                            <AnchorHeading id="the-machine">The machine every dispute goes through</AnchorHeading>
                            <p>
                                The pipeline has six stops, and your letter only controls the first one. Everything after the clerk is
                                automation resolving a code against a database.
                            </p>
                            <figure className="dw-home-clerk__flow dw-post__figure">
                                <FigureCaptionLine>Fig. 1 · The machine every dispute goes through</FigureCaptionLine>
                                <div className="dw-home-clerk__flow-strip">
                                    {FLOW.map((step, index) => (
                                        <span key={step} className="dw-home-clerk__flow-group">
                                            <span className="dw-text-body dw-tabular-nums" data-tone="foreground">{step}</span>
                                            {index < FLOW.length - 1 && (
                                                <span className="dw-text-body dw-tabular-nums" data-tone="muted" aria-hidden>
                                                    →
                                                </span>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            </figure>
                            <p>
                                A dispute succeeds when its claim survives code compression intact and lands in a form the furnisher&apos;s
                                automation cannot resolve against its own record. That is the entire game. Not persuasion. Survival.
                            </p>

                            <AnchorHeading id="writing-for-the-machine">Writing for the machine</AnchorHeading>
                            <p>
                                Fluid writes for the machine. One letter, one Metro 2® field contradiction, one code for the clerk to select,
                                one claim the furnisher&apos;s own data cannot refute. DisputeWell runs 48 field-level compliance checks from
                                the 2023 CRRG against every tradeline, so a claim starts as a data structure, not a sentence.
                            </p>
                            <p>
                                And when an account gives it nothing to work with, it does not dispute. No grounds, no dispute. That is a
                                rule, not a setting.
                            </p>
                            <figure className="dw-home-clerk__matrix dw-post__figure">
                                <FigureCaptionLine>Table 1 · The industry, stage by stage</FigureCaptionLine>
                                <div role="table">
                                    <div className="dw-home-clerk__matrix-row" data-head="" role="row">
                                        <span className="dw-text-caption dw-tabular-nums" data-tone="muted" role="columnheader">Stage</span>
                                        <span className="dw-text-caption dw-tabular-nums" data-tone="muted" role="columnheader">The industry</span>
                                        <span className="dw-text-caption dw-tabular-nums" data-tone="muted" role="columnheader">DisputeWell</span>
                                    </div>
                                    {MATRIX.map((row) => (
                                        <div key={row.stage} className="dw-home-clerk__matrix-row" role="row">
                                            <span className="dw-text-lead" data-tone="foreground" role="cell">{row.stage}</span>
                                            <span className="dw-text-body" data-tone="muted" role="cell"><s>{row.industry}</s></span>
                                            <span className="dw-text-body" data-tone="foreground" role="cell">{row.disputewell}</span>
                                        </div>
                                    ))}
                                </div>
                            </figure>

                            <AnchorHeading id="round-two">Round two is a different legal instrument</AnchorHeading>
                            <p>
                                Most software treats round two as round one, reworded. The law treats it as a different instrument. What the
                                bureau did with round one determines which statute round two invokes, and Fluid selects the escalation from
                                the outcome on record.
                            </p>
                            <figure className="dw-home-clerk__ladder dw-post__figure">
                                <FigureCaptionLine>Fig. 2 · Round two, selected by what actually happened</FigureCaptionLine>
                                <LeaderList rows={LADDER} alignValueEnd />
                            </figure>
                            <p>
                                Deletions feed the next strategy. If an item comes back after a deletion, the system catches the reinsertion on
                                the next import and drafts the §611(a)(5)(B) notice. You are never asking the machine the same question twice.
                            </p>

                            <AnchorHeading id="what-we-dont-claim">What we don&apos;t claim</AnchorHeading>
                            <p>
                                An industry this crowded with overclaiming deserves a short list of what we will not say.
                            </p>
                            <ul>
                                {DONT.map((item) => (
                                    <li key={item.title}>
                                        <strong>{item.title}.</strong> {item.body}
                                    </li>
                                ))}
                            </ul>
                            <p>
                                The machine is not going away. The letter that beats it is the one written for it.
                            </p>
                        </div>

                        <footer className="dw-post__footer dw-text-body">
                            <p>
                                <span data-tone="muted">Filed under: </span>
                                <Link href="/blog" data-tone="foreground">research</Link>
                            </p>
                            <p data-tone="muted">Authors: Kane Traylor, Dylan Shaw</p>
                        </footer>
                    </div>
                </article>

                <section className="dw-post__related" aria-label="Related posts">
                    <div className="dw-post__rail">
                        <h2 className="dw-text-lead" data-tone="foreground">Related posts</h2>
                    </div>
                    <div className="dw-post__column">
                        <div className="dw-post__related-list">
                            {RELATED.map((post) => (
                                <Link key={post.title} href="/blog" className="dw-blog__log">
                                    <PostMeta dateISO={post.dateISO} dateLabel={post.dateLabel} category={post.category} />
                                    <p className="dw-text-body" data-tone="foreground">{post.title}</p>
                                    <p className="dw-text-body" data-tone="muted">{post.byline}</p>
                                </Link>
                            ))}
                        </div>
                        <Link href="/blog" className="dw-blog__strip-more dw-text-body dw-tabular-nums" data-tone="primary">
                            View more posts →
                        </Link>
                    </div>
                </section>
            </main>

            <SiteFooter />
        </div>
    );
});
